import type { RoomPlayerPayload, RoomStatePayload } from '@hazel/shared'

/**
 * What the server sends, taken from the shared schemas rather than described
 * again here.
 *
 * These used to be hand-written interfaces, and `RoomState.game` was a lie:
 * typed as Hangman's view long after the server had started sending a tagged
 * round. The socket handler cast whatever arrived to it, so nothing failed to
 * compile and every test on both sides passed. Deriving them means the client
 * cannot hold an opinion about the wire format that the server does not share.
 */
export type RoomPlayer = RoomPlayerPayload
export type RoomState = RoomStatePayload

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting'

/** Reasons the server can refuse something, mapped to words a child can read. */
export const ROOM_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  'no-such-room': "We couldn't find that game. Check the code on the telly.",
  'room-full': 'That game is full — no more room, sorry.',
  'name-required': 'Pop your name in first.',
  'not-your-turn': "It's not your go yet.",
  'already-played': 'That letter has been tried already.',
  'game-over': 'That round has finished.',
  'invalid-action': "That didn't work — try again.",
  'not-seated': "You're not in this game.",
  'no-round': 'Waiting for the next round to start.',
  'cannot-start': 'Nobody has joined yet.',
  'unknown-player': 'You’re in for the next one — this round had already started.',
  'bad-request': "That didn't work — try again.",
  'server-busy': 'Too many games are running. Try again in a minute.',
}

export function friendlyRoomError(reason: string): string {
  return ROOM_ERROR_MESSAGES[reason] ?? "That didn't work — try again."
}
